const API_BASE_URL = '/api';

class ApiClient {
    constructor() {
        this.baseURL = API_BASE_URL;
    }

    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;

        const config = {
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
            ...options,
        };

        // Handle FormData (for file uploads)
        if (options.body instanceof FormData) {
            // Remove Content-Type header to let browser set it with boundary
            delete config.headers['Content-Type'];
        }

        try {
            const response = await fetch(url, config);

            // Check if response is JSON
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Non-JSON response:', text);
                throw new Error('Server returned non-JSON response');
            }

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || data.message || 'API request failed');
            }

            return data;
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    }

    // Portfolio methods
    async getPortfolio(featured = false, limit = null) {
        const params = new URLSearchParams();

        if (featured) {
            params.append('featured', 'true');
        }

        if (limit !== null) {
            params.append('limit', limit);
        }

        const query = params.toString() ? `?${params.toString()}` : '';
        return this.request(`/portfolio.php${query}`);
    }

    async getPortfolioItem(slug) {
        return this.request(`/portfolio.php?slug=${slug}`);
    }

    // News methods
    async getNews(featured = false, limit = null, category = null, search = null) {
        const params = new URLSearchParams();

        if (featured) {
            params.append('featured', 'true');
        }

        if (limit !== null) {
            params.append('limit', limit);
        }

        if (category) {
            params.append('category', category);
        }

        if (search) {
            params.append('search', search);
        }

        const query = params.toString() ? `?${params.toString()}` : '';
        return this.request(`/news.php${query}`);
    }

    async getNewsArticle(slug) {
        return this.request(`/news.php?slug=${slug}`);
    }

    async getFeaturedNews(limit = 3) {
        return this.getNews(true, limit);
    }

    async getRecentNews(limit = 6) {
        return this.getNews(false, limit);
    }

    async searchNews(query, limit = 10) {
        return this.getNews(false, limit, null, query);
    }

    async getNewsByCategory(category, limit = 10) {
        return this.getNews(false, limit, category);
    }

    // Contact form submission
    async submitContactForm(formData) {
        // formData should already be a FormData object from the frontend
        if (!(formData instanceof FormData)) {
            throw new Error('Contact form data must be FormData object for file uploads');
        }

        return this.request('/contact.php', {
            method: 'POST',
            body: formData,
        });
    }

    // Consultation form submission
    async submitConsultationForm(formData) {
        // formData should already be a FormData object from the frontend
        if (!(formData instanceof FormData)) {
            throw new Error('Consultation form data must be FormData object for file uploads');
        }

        return this.request('/consultation.php', {
            method: 'POST',
            body: formData,
        });
    }

    // Consultation booking (legacy method - keeping for backward compatibility)
    async bookConsultation(bookingData) {
        return this.request('/consultation.php', {
            method: 'POST',
            body: JSON.stringify(bookingData),
        });
    }

    // EAA Compliance Scanner
    async scanEAACompliance(scanData) {
        return this.request('/eaa-scanner.php', {
            method: 'POST',
            body: JSON.stringify(scanData),
        });
    }

    // Generate PDF Report
    async generatePDFReport(scanId, recaptchaToken) {
        const url = `${this.baseURL}/generate-pdf-report.php`;
        
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                scan_id: scanId,
                recaptchaToken: recaptchaToken
            }),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'PDF generation failed');
        }

        // Return blob for download
        return response.blob();
    }
}

// Create and export a singleton instance
const apiClient = new ApiClient();

export default apiClient;

// Named exports for convenience
export const {
    getPortfolio,
    getPortfolioItem,
    getNews,
    getNewsArticle,
    getFeaturedNews,
    getRecentNews,
    searchNews,
    getNewsByCategory,
    submitContactForm,
    submitConsultationForm,
    bookConsultation,
    scanEAACompliance,
    generatePDFReport
} = apiClient;